import {
  Dispatch,
  HTMLAttributes,
  SetStateAction,
  useCallback,
  useEffect,
} from 'react';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {getFormattedNumberWithSuffix} from '../../../../common/shared/utils/getFormattedNumberWithSuffix';
import {twMerge} from '../../../../common/twMerge';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {BelowDefaultTab} from '../../../../design-system/organisms/BelowDefaultTab/BelowDefaultTab';
import {Maybe, SortOption} from '../../../../graphql/generated/types';
import {useInfiniteCommentListQuery} from '../../../../wrapper';
import {useCommentSortOptDialog} from '../../../comment/hook/useCommentSortOptDialog';

export interface ContentHomeCommentTabProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  selectedSortOpt: Maybe<SortOption>;
  setSelectedSortOpt: Dispatch<SetStateAction<Maybe<SortOption>>>;
}

export function ContentHomeCommentTab({
  seriesId,
  selectedSortOpt,
  setSelectedSortOpt,
  children,
  className,
  ...props
}: ContentHomeCommentTabProps) {
  const {isMobile} = requestContext.requestInfo.display;

  const {data: commentList} = useInfiniteCommentListQuery(
    {
      commentListInput: {
        page: 0,
        seriesId,
        sortType: selectedSortOpt?.param,
      },
    },
    {
      initialPageParam: {
        commentListInput: {
          page: 0,
          seriesId,
          sortType: selectedSortOpt?.param,
        },
      },
      getNextPageParam: () => undefined,
    }
  );

  const {sortOptList, totalCount} = commentList?.pages[0].commentList || {};

  useEffect(() => {
    if (commentList?.pages[0].commentList?.selectedSortOpt) {
      setSelectedSortOpt(commentList.pages[0].commentList.selectedSortOpt);
    }
  }, [commentList, setSelectedSortOpt]);
  const onSelectSortOption = useCallback(
    (sortOptIndex: number) => {
      if (!sortOptList) return;
      setSelectedSortOpt(sortOptList[sortOptIndex]);
    },
    [setSelectedSortOpt, sortOptList]
  );

  const {showDialog, commentSortOptDialog} = useCommentSortOptDialog({
    isMobile,
    sortOptList,
    selectedSortOpt,
    onSelectSortOption,
  });
  return (
    <div className={className} {...props}>
      {selectedSortOpt && (
        <TabPositionControl
          depth={2}
          isMobile={true}
          isWebView={false}
          disabled={!isMobile}
          forceSticky={true}
        >
          <BelowDefaultTab
            className={twMerge(
              'px-18pxr',
              isMobile ? 'bg-theme-bg-base' : 'bg-theme-bg-surface-alt'
            )}
          >
            <BelowDefaultTab.TextLabel className="font-small2-bold text-theme-solid-100">
              {t('common_all_count', {
                values: {
                  value: totalCount
                    ? getFormattedNumberWithSuffix(totalCount)
                    : 0,
                },
              })}
            </BelowDefaultTab.TextLabel>
            <BelowDefaultTab.SelectBox
              iconType={IconType.ContentHomeFilter}
              onClick={showDialog}
            >
              {selectedSortOpt.name}
            </BelowDefaultTab.SelectBox>
          </BelowDefaultTab>
        </TabPositionControl>
      )}
      {children}
      {commentSortOptDialog}
    </div>
  );
}
